import { isObject } from "#src/utils/is";
import { message } from "#src/utils/static-antd";
import { createElement } from "react";

interface AbpValidationError { message?: string | null }
interface AbpError {
	message?: string | null
	details?: string | null
	validationErrors?: AbpValidationError[] | null
}
interface ErrorEnvelope {
	error?: AbpError | string | null
	details?: string | null
	errorMsg?: string
	message?: string
}

function buildErrorText(data: ErrorEnvelope, fallback: string): string {
	const topLevelDetails = typeof data?.details === "string" ? data.details.trim() : "";
	const topLevelError = typeof data?.error === "string" ? data.error.trim() : "";

	if (topLevelDetails) {
		return topLevelError ? `${topLevelError}\n${topLevelDetails}` : topLevelDetails;
	}
	if (topLevelError)
		return topLevelError;

	const e = typeof data?.error === "object" && data.error !== null ? data.error : null;
	if (e?.details && typeof e.details === "string" && e.details.trim() !== "")
		return e.details;
	if (e) {
		const parts: string[] = [];
		if (e.message)
			parts.push(e.message);
		for (const v of e.validationErrors ?? []) {
			if (v?.message)
				parts.push(v.message);
		}
		if (parts.length > 0)
			return parts.join("\n");
	}
	return data?.errorMsg || data?.message || fallback;
}

/**
 * @en Handles the error response
 *
 * @param response The response object
 * @returns The response object
 */
export async function handleErrorResponse(response: Response) {
	const fallback = response.statusText;

	try {
		// @en Parse the response content as JSON format
		const data = await response.json();

		// @en Check if the parsed data is an object type
		if (isObject(data)) {
			const text = buildErrorText(data as ErrorEnvelope, fallback);
			message.error({
				content: createElement("span", { style: { whiteSpace: "pre-line" } }, text),
				duration: 6,
			});
		} else {
			// @en If the parsed data is not an object type, display the response status text directly as the error message.
			message.error({
				content: createElement("span", { style: { whiteSpace: "pre-line" } }, fallback),
				duration: 6,
			});
		}
	} catch (e) {
		// @en If an error occurs during JSON parsing, print the error information to the console.
		console.error("Error parsing JSON:", e);

		// @en Display the response status text as the error message.
		message.error({
			content: createElement("span", { style: { whiteSpace: "pre-line" } }, fallback),
			duration: 6,
		});
	}

	// @en Return the response object
	return response;
}
